import { Fragment, useId, useState } from 'react';
import { ChevronDown, ChevronRight, Wallet } from 'lucide-react';
import { formatDate, formatDateTime, labelOf, OP_TYPE_LABELS } from '@blimo/shared';
import { Badge } from '../components/badge.jsx';
import { EmptyState } from '../components/empty-state.jsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/table.jsx';
import { OpenItemAmount } from './amounts.jsx';

/**
 * Open items grouped by property → tenant account (GET …/finance/open-items). Amounts come in
 * display sign: negative = arrears (red), positive = credit. Totals are taken from the API.
 * @param {{ data: any, showPropertyHeading?: boolean, emptyDescription?: string }} props
 */
export function OpenItemsView({ data, showPropertyHeading = true, emptyDescription }) {
  const properties = data?.properties ?? [];
  if (properties.length === 0) {
    return (
      <EmptyState
        icon={Wallet}
        title="Keine offenen Posten"
        description={emptyDescription ?? 'Für die gewählte Auswahl sind derzeit keine offenen Posten gebucht.'}
      />
    );
  }
  return (
    <div className="flex flex-col gap-4">
      {properties.length > 1 && (
        <p className="flex flex-wrap items-baseline gap-2 text-sm">
          <span className="text-muted-foreground">Summe aller Liegenschaften</span>
          <OpenItemAmount value={data.total} className="text-base font-semibold" />
        </p>
      )}
      {properties.map((property) => (
        <section
          key={property.property_id}
          aria-label={`Offene Posten ${property.name ?? ''}`.trim()}
          data-slot="finance-section"
          className="flex flex-col gap-3 rounded-lg border bg-card p-3 sm:p-4"
        >
          <header className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
            {showPropertyHeading ? <h2 className="text-h3 font-semibold">{property.name}</h2> : <span />}
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
              <span>
                Summe <OpenItemAmount value={property.total} className="font-semibold" />
              </span>
              <span className="text-xs text-muted-foreground">Stand: {property.synced_at ? formatDateTime(property.synced_at) : 'unbekannt'}</span>
            </div>
          </header>
          {property.accounts.map((account) => (
            <AccountBlock key={account.account_number} account={account} />
          ))}
        </section>
      ))}
    </div>
  );
}

function AccountBlock({ account }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex flex-wrap items-center gap-2 text-sm font-semibold">
          <span>
            Konto <span className="numeric">{account.account_number}</span>
          </span>
          {account.from_past_contract && <Badge variant="neutral">aus altem Vertrag</Badge>}
        </h3>
        <span className="text-sm">
          <span className="text-muted-foreground">Kontosumme </span>
          <OpenItemAmount value={account.total} className="font-semibold" />
        </span>
      </div>
      <Table aria-label={`Offene Posten Konto ${account.account_number}`} containerClassName="rounded-md border">
        <TableHeader>
          <TableRow>
            <TableHead>Fälligkeit</TableHead>
            <TableHead>Buchungstext</TableHead>
            <TableHead>Art</TableHead>
            <TableHead>Mahnstufe</TableHead>
            <TableHead data-numeric="true">Betrag</TableHead>
            <TableHead>
              <span className="sr-only">Aufteilung</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {account.items.map((item, index) => (
            <ItemRow key={`${item.booking_number ?? 'x'}-${index}`} item={item} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ItemRow({ item }) {
  const [open, setOpen] = useState(false);
  const splits = item.splits ?? [];
  const level = Number(item.dunning_level) || 0;
  const splitId = useId();
  return (
    <Fragment>
      <TableRow>
        <TableCell className="numeric">{formatDate(item.due_on)}</TableCell>
        <TableCell className="min-w-40">
          {item.text || '–'}
          {item.booking_number && <span className="numeric block text-xs text-muted-foreground">Beleg {item.booking_number}</span>}
        </TableCell>
        <TableCell className="whitespace-nowrap">{labelOf(OP_TYPE_LABELS, item.op_type)}</TableCell>
        <TableCell>
          {level > 0 ? <Badge variant={level >= 2 ? 'destructive' : 'warning'}>Mahnstufe {level}</Badge> : <span className="text-muted-foreground">–</span>}
        </TableCell>
        <TableCell data-numeric="true">
          <OpenItemAmount value={item.amount} />
        </TableCell>
        <TableCell className="w-10 text-right">
          {splits.length > 0 && (
            <button
              type="button"
              aria-expanded={open}
              aria-controls={splitId}
              onClick={() => setOpen((v) => !v)}
              className="inline-flex min-h-8 items-center gap-1 rounded px-1.5 text-xs text-primary hover:underline"
            >
              {open ? <ChevronDown className="size-3.5" aria-hidden="true" /> : <ChevronRight className="size-3.5" aria-hidden="true" />}
              Aufteilung ({splits.length})
            </button>
          )}
        </TableCell>
      </TableRow>
      {open && (
        <TableRow id={splitId} className="bg-muted/40 hover:bg-muted/40">
          <TableCell colSpan={6} className="py-2">
            <ul className="ml-auto flex max-w-sm flex-col gap-1 text-sm" aria-label="Aufteilung">
              {splits.map((split, i) => (
                <li key={`${split.type}-${i}`} className="flex items-center justify-between gap-4">
                  <span className="text-muted-foreground">{split.type}</span>
                  <OpenItemAmount value={split.amount} />
                </li>
              ))}
            </ul>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
}
